import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { loanApi } from '../../../api/loanApi';
import {
  isNeverDisbursed, LoanListItemDto, LoanStatusColors, LoanStatusLabels, LoanTypeLabels,
} from '../../../types/loanTypes';
import LoanFormDialog from '../components/LoanFormDialog';

const money = (value: number) =>
  `₹${(value ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const MyLoansPage: React.FC = () => {
  const navigate = useNavigate();
  const [loans, setLoans] = useState<LoanListItemDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setLoans(await loanApi.getMyLoans());
    } catch {
      toast.error('Failed to load your loans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={700}>My Loans & Advances</Typography>
        <Button variant="contained" startIcon={<AddIcon />} sx={{ textTransform: 'none' }} onClick={() => setDialogOpen(true)}>
          Request Loan / Advance
        </Button>
      </Stack>

      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Reference</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Principal</TableCell>
                  <TableCell align="right">Outstanding</TableCell>
                  <TableCell align="center">Installments</TableCell>
                  <TableCell>Recovery From</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loans.length === 0 ? (
                  <TableRow><TableCell colSpan={7} align="center">You have no loans or advances</TableCell></TableRow>
                ) : loans.map((loan) => (
                  <TableRow key={loan.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/loans/my/${loan.id}`)}>
                    <TableCell>{loan.referenceNumber}</TableCell>
                    <TableCell>{LoanTypeLabels[loan.loanType]}</TableCell>
                    <TableCell align="right">{money(loan.principalAmount)}</TableCell>
                    <TableCell align="right">
                      {isNeverDisbursed(loan.status)
                        ? <Typography variant="body2" color="text.disabled">—</Typography>
                        : money(loan.outstandingBalance)}
                    </TableCell>
                    <TableCell align="center">{loan.installmentsPaid}/{loan.totalInstallments}</TableCell>
                    <TableCell>{MONTHS[loan.repaymentStartMonth - 1]} {loan.repaymentStartYear}</TableCell>
                    <TableCell><Chip size="small" label={LoanStatusLabels[loan.status]} color={LoanStatusColors[loan.status]} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <LoanFormDialog open={dialogOpen} mode="employee" onClose={() => setDialogOpen(false)} onSaved={load} />
    </Box>
  );
};

export default MyLoansPage;
